import { Link } from "@/components/app/localized-link";
import { Image } from "./image";
import { getImage } from "@/lib/getImage";
import { Skeleton } from "../ui/skeleton";
import type { SingleOffer } from "@/types/single-offer";
import { offersDictionary } from "@/lib/offers-dictionary";
import { useEffect, useMemo } from "react";
import { useGenres } from "@/hooks/use-genres";
import { cn } from "@/lib/utils";
import { Badge } from "../ui/badge";
import { calculatePrice } from "@/lib/calculate-price";
import { useLocale } from "@/hooks/use-locale";
import { platformIcons } from "./platform-icons";
import useExtension from "@/hooks/use-extension";
import { getEffectivePrice } from "@/lib/effective-price";

export function GameCard({ offer }: { offer: SingleOffer }) {
  const { locale } = useLocale();
  const price = getEffectivePrice(offer.price);
  const fmt = Intl.NumberFormat(locale, {
    style: "currency",
    currency: price?.price.currencyCode || "USD",
  });

  const isReleased = offer.releaseDate ? new Date(offer.releaseDate) < new Date() : false;
  const isPreOrder = offer.prePurchase;
  const isFree = price?.price.discountPrice === 0;

  const gradientImage = getImage(offer.keyImages, [
    "OfferImageTall",
    "Thumbnail",
    "DieselGameBoxTall",
    "DieselStoreFrontTall",
  ])?.url;

  return (
    <Link
      to="/{-$locale}/offers/$id"
      params={{ id: offer.id }}
      preload="viewport"
      aria-label={`Open offer ${offer.title}`}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-md bg-card aspect-[3/4] relative group">
        <Image
          src={gradientImage}
          quality="medium"
          alt={offer.title}
          width={400}
          height={500}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />
        {offer.offerType && (
          <span className="absolute top-2 left-2 bg-background/60 backdrop-blur-sm text-foreground/90 text-[0.65rem] font-semibold px-2 py-1 rounded uppercase tracking-wide">
            {offersDictionary[offer.offerType as keyof typeof offersDictionary]}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-3 z-10">
          <h3 className="text-lg font-display font-semibold text-foreground leading-tight line-clamp-2">
            {offer.title}
          </h3>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate">{offer.seller.name}</span>
            <div className="inline-flex items-center gap-2 justify-end">
              {isReleased && price && (
                <>
                  {price.price.discount > 0 && (
                    <span className="text-xs text-muted-foreground line-through">
                      {fmt.format(price.price.originalPrice / 100)}
                    </span>
                  )}
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      isFree || price.price.discount > 0 ? "text-primary" : "text-foreground",
                    )}
                  >
                    {isFree ? "Free" : fmt.format(price.price.discountPrice / 100)}
                  </span>
                </>
              )}
              {!isReleased && isPreOrder && price?.price.discountPrice !== undefined && (
                <span className="text-sm font-semibold text-foreground">
                  {fmt.format(price.price.discountPrice / 100)}
                </span>
              )}
              {!isReleased && !isPreOrder && !price && (
                <span className="text-sm font-semibold text-muted-foreground">Coming Soon</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function GameCardSkeleton() {
  return (
    <div className="w-full max-w-sm overflow-hidden rounded-md bg-card aspect-[3/4] relative">
      <Skeleton className="w-full h-full absolute inset-0" />
    </div>
  );
}

const textSizes = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
};

const mobilePlatforms = ["39070", "39071"];

export function OfferCard({
  offer,
  size = "xl",
}: {
  offer: SingleOffer;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}) {
  const { addId, removeId, ownedStatus } = useExtension();
  const { genres } = useGenres();

  const offerGenres = useMemo(() => {
    if (!genres) return [];
    const genreIds = genres.map((genre) => genre.id);
    return offer.tags
      .filter((tag) => tag)
      .filter((tag) => genreIds.includes(tag?.id))
      .map((tag) => tag.name);
  }, [genres, offer.tags]);

  const gradientImage = useMemo(
    () =>
      getImage(offer.keyImages, [
        "OfferImageTall",
        "Thumbnail",
        "DieselGameBoxTall",
        "DieselStoreFrontTall",
      ])?.url ?? "/placeholder.webp",
    [offer.keyImages],
  );

  useEffect(() => {
    addId(offer.id, offer.namespace);
    return () => removeId(offer.id, offer.namespace);
  }, [addId, removeId, offer]);

  const owned = ownedStatus[`${offer.id}:${offer.namespace}`];

  return (
    <Link
      to="/{-$locale}/offers/$id"
      params={{
        id: offer.id,
      }}
      preload="viewport"
      className="select-none group mx-auto w-fit md:w-full"
      viewTransition
      aria-label={`Open offer ${offer.title}`}
    >
      <div className="relative w-64 md:w-full overflow-hidden rounded-md bg-card aspect-[3/4]">
        <Image
          src={gradientImage}
          alt={offer.title}
          width={600}
          height={800}
          quality="high"
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />
        {offer.offerType && (
          <span className="absolute top-2 left-2 bg-background/60 backdrop-blur-sm text-foreground/90 text-[0.65rem] font-semibold px-2 py-1 rounded uppercase tracking-wide">
            {offersDictionary[offer.offerType as keyof typeof offersDictionary]}
          </span>
        )}
        <OfferBadges offer={offer} owned={owned} />
        <div className="absolute inset-x-0 bottom-0 p-3 z-10">
          <h3
            className={cn(
              "font-display font-semibold text-foreground leading-tight line-clamp-2 drop-shadow-md",
              textSizes[size] ?? textSizes.xl,
            )}
          >
            {offer.title}{" "}
            <span className="inline-flex items-center gap-1.5 align-middle">
              {offer.tags
                .filter((tag) => tag)
                .filter((tag) => mobilePlatforms.includes(tag?.id))
                .map((tag) => (
                  <span key={tag?.id} className="scale-90">
                    {platformIcons[tag?.id]}
                  </span>
                ))}
            </span>
          </h3>
          <div className="mt-1 max-h-0 overflow-hidden group-hover:max-h-24 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out">
            <p className="text-xs text-muted-foreground mb-2">
              {offerGenres.length > 0
                ? offerGenres.join(", ")
                : offersDictionary[offer.offerType as keyof typeof offersDictionary]}
            </p>
          </div>
          <div className="mt-1.5">
            <OfferPrice offer={offer} size={size} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function OfferBadges({ offer, owned }: { offer: SingleOffer; owned: boolean | undefined }) {
  const badges = useMemo(() => {
    const badges: string[] = [];

    if (offer.tags.filter((tag) => tag).find((tag) => tag?.id === "1310")) {
      badges.push("Early Access");
    }

    if (offer.prePurchase) {
      badges.push("Pre-Purchase");
    }

    if (owned === true) {
      badges.push("Owned");
    }

    return badges;
  }, [offer.tags, offer.prePurchase, owned]);

  return badges.length > 0 ? (
    <Badge
      variant={"default"}
      className="absolute top-2 right-2 bg-primary text-primary-foreground"
    >
      {badges.join(" - ")}
    </Badge>
  ) : null;
}

function OfferPrice({
  offer,
  size = "xl",
}: {
  offer: SingleOffer;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}) {
  const { locale } = useLocale();
  const price = getEffectivePrice(offer.price);
  const fmt = Intl.NumberFormat(locale, {
    style: "currency",
    currency: price?.price.currencyCode || "USD",
  });

  const isReleased = offer.releaseDate ? new Date(offer.releaseDate) < new Date() : false;
  const isPreOrder = offer.prePurchase;
  const isFree = price?.price.discountPrice === 0;

  if (!price) return null;

  const discountTextSizes = {
    xs: "text-[0.6rem]",
    sm: "text-[0.6rem]",
    md: "text-[0.8rem]",
    lg: "text-[1rem]",
    xl: "text-[1.125rem]",
  };

  const textSizes = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-md",
    lg: "text-lg",
    xl: "text-xl",
  };

  const formatPrice = (amount: number) =>
    fmt.format(calculatePrice(amount, price.price.currencyCode));

  const isDiscounted = price.price.discount > 0;

  const renderPrice = () => (
    <>
      <span className={isFree || isDiscounted ? "text-primary" : "text-foreground"}>
        {isFree ? "Free" : formatPrice(price.price.discountPrice)}
      </span>
      {isDiscounted && (
        <span
          className={cn(
            "line-through text-muted-foreground",
            discountTextSizes[size] ?? discountTextSizes.xl,
          )}
        >
          {formatPrice(price.price.originalPrice)}
        </span>
      )}
    </>
  );

  const renderDiscountBadge = () => (
    <div className="text-xs inline-flex items-center rounded-full bg-primary text-primary-foreground px-2 py-1 font-semibold shadow-sm">
      {`-${Math.round(
        ((price.price.originalPrice - price.price.discountPrice) / price.price.originalPrice) * 100,
      )}%`}
    </div>
  );

  return (
    <div
      className={cn(
        "text-lg font-bold inline-flex items-end gap-2 z-10 h-full justify-between",
        textSizes[size] ?? textSizes.xl,
      )}
    >
      <div className="inline-flex items-center gap-2">
        {isReleased ? (
          renderPrice()
        ) : isPreOrder ? (
          renderPrice()
        ) : price.price.discountPrice !== 0 ? (
          renderPrice()
        ) : (
          <span className="text-muted-foreground">Coming Soon</span>
        )}
      </div>
      {price.price.discount > 0 && renderDiscountBadge()}
    </div>
  );
}
